/**
 * Reading a key's exposure record for display — shared by the key detail's
 * headline ("This key might be exposed to …") and the Exposure dialog.
 */
import { erasedOwnerEmail, CAP_ROLES } from '@papan/types'
import type { ExposureItem, KeybookExposure } from '../client/api.js'

export type ExposureKind = 'role' | 'team' | 'email' | 'app' | 'tenant'
export interface ExposureEntity { kind: ExposureKind; item: ExposureItem }

type T = (key: string, opts?: Record<string, unknown>) => string

/** Role labels — the official names (`CAP_ROLES`: "Drive admin", "Member"…),
 *  as the access dialog shows them; the organization owner (recorded as role
 *  `owner`) is translated. */
export const exposureRoleLabel = (t: T, id: string): string =>
  (id === 'owner' ? t('exposure.roleOwner') : CAP_ROLES.find(r => r.id === id)?.label ?? id)

/** "jane.doe@x" → "Jane Doe" — the access dialog's derivation. */
export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email
  return local.split(/[._-]+/).filter(Boolean).map(s => s[0]!.toUpperCase() + s.slice(1)).join(' ') || email
}

/** Roles in display order: the organization owner first. */
const ROLE_ORDER = ['owner', 'admin', 'member', 'viewer', 'guest']
const roleRank = (id: string) => { const i = ROLE_ORDER.indexOf(id); return i < 0 ? ROLE_ORDER.length : i }

/**
 * Every entity in the current period's record (model C: principals and the people
 * who opened it), in display order: roles (the organization owner first), teams,
 * people, PDApps, outside orgs.
 */
export function exposureEntities(x: KeybookExposure): ExposureEntity[] {
  return [
    ...[...x.groups.role].sort((a, b) => roleRank(a.id) - roleRank(b.id)).map(item => ({ kind: 'role' as const, item })),
    ...x.groups.team.map(item => ({ kind: 'team' as const, item })),
    // The person who is the organization owner NOW is the "Organization owner"
    // role entry above — not listed (or counted) a second time as a person,
    // UNLESS they opened it: a reader record is evidence the role entry doesn't
    // carry ("Opened <date>"), so it stays.
    ...x.groups.email.filter(item => !item.isOrganizationOwner || !!item.openedAt).map(item => ({ kind: 'email' as const, item })),
    ...x.groups.app.map(item => ({ kind: 'app' as const, item })),
    ...x.groups.tenant.map(item => ({ kind: 'tenant' as const, item })),
  ]
}

/** An entity's display name: a person's name (or email), a team's name, a role's label. */
export function exposureEntityName(t: T, { kind, item }: ExposureEntity): string {
  if (kind === 'role') return exposureRoleLabel(t, item.id)
  if (kind === 'email') {
    const erased = erasedOwnerEmail(item.id)
    return erased ? t('ownerDeletedAccount', { email: erased }) : (item.name || item.id)
  }
  return item.name ?? item.id
}

/** How a person can open it NOW, in words (an email row's `via`); "No access now"
 *  when they can't. */
export function exposureViaLabel(t: T, via: ExposureItem['via']): string {
  if (!via) return t('exposure.via.noAccessNow')
  // A collection's name comes only to a viewer who manages it (the server).
  const where = via.level === 'collection'
    ? (via.collectionName ? t('exposure.via.onCollection', { name: via.collectionName }) : t('exposure.via.onSomeCollection'))
    : via.level === 'root' ? t('exposure.via.keybookWide') : null
  const withWhere = (s: string) => (where ? `${s} · ${where}` : s)
  if (via.type === 'team') return withWhere(t('exposure.via.team', { name: via.name ?? t('access.teamSub') }))
  if (via.type === 'role') return withWhere(t('exposure.via.role', { name: exposureRoleLabel(t, via.id ?? '') }))
  if (via.type === 'direct') {
    return via.level === 'collection'
      ? (via.collectionName ? t('exposure.via.directCollection', { name: via.collectionName }) : t('exposure.via.directSomeCollection'))
      : via.level === 'root' ? t('exposure.via.directRoot')
        : t('exposure.via.direct')
  }
  if (via.type === 'admin-default') return t('exposure.via.adminDefault')
  if (via.type === 'owner') return t('exposure.via.owner')
  return t('exposure.via.emailOwner')
}

/** A short date ("12 Oct 2026") in the reader's locale. */
export function shortDate(iso: string, lang?: string): string {
  return new Date(iso).toLocaleDateString(lang || undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
